import { CancelOfferCharge, GetCancelOfferReply } from 'proto/billing-manager_pb';
import { formatFreezeMoney } from 'features/subscription-freeze/formatters';
import { formatEndsOn } from 'sections/account-settings/subscriptions/checkout-v2/Configure/formatEndsOn';
import { PlanGroup } from './billing';
import { OFFER_STEP } from './content';

export type CancelOffer = GetCancelOfferReply.AsObject;
/** An offer open to them: billing named the renewal it goes to. */
export type ReadyCancelOffer = CancelOffer & { nextCharge: CancelOfferCharge.AsObject };

// `struck` marks the price the offer replaces.
export type OfferRow = { label: string; value: string; struck?: boolean };
export type OfferSection = { title?: string; rows: OfferRow[] };
// `note` is the rule under the offer: when it applies and what does not use it.
export type OfferCardView = { headline: string; pill: string; sections: OfferSection[]; note: { title: string; body: string } };
// The plans the renewal bills, as the confirm step lists them.
export type OfferRenewal = { label: string; plans: PlanGroup[] };
export type CancelOfferView = { title: string; description: string; card: OfferCardView; renewal: OfferRenewal };

/** The plans a subscription bills, found by its Stripe id, which is how billing-manager names the renewal. */
export type RenewalPlans = (stripeSubscriptionId: string) => PlanGroup[];

export const offerPercent = (bps: number) => `${bps / 100}%`;

/** The day of the renewal an offer goes to, when billing named one. */
export const offerChargeDate = (charge?: CancelOfferCharge.AsObject) => (charge && formatEndsOn(charge.chargeAt)) || undefined;

/** The "too expensive" offer: the percent off their next payment, priced both ways, and the plans that renewal bills. */
export function cancelOfferView(offer: ReadyCancelOffer, renewalPlans: RenewalPlans): CancelOfferView {
  const percent = offerPercent(offer.percentBps);
  const charge = offer.nextCharge;
  const money = (amount: number) => formatFreezeMoney(amount, charge.currency);
  const rows: OfferRow[] = [
    { label: OFFER_STEP.yourPrice, value: money(charge.offerAmount) },
    {
      label: OFFER_STEP.insteadOf,
      value: OFFER_STEP.pricePer(money(charge.chargeAmount), charge.interval, charge.intervalCount),
      struck: true
    },
    { label: OFFER_STEP.commitment, value: OFFER_STEP.cancelAnytime }
  ];
  return {
    title: OFFER_STEP.title(percent),
    description: OFFER_STEP.description(percent),
    card: { headline: OFFER_STEP.headline(percent), pill: OFFER_STEP.pill, sections: [{ rows }], note: OFFER_STEP.note },
    renewal: { label: OFFER_STEP.renewsOn(offerChargeDate(charge)), plans: renewalPlans(charge.subscriptionId) }
  };
}
